import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    Eye,
    Trash2,
    Package,
    AlertCircle,
    DollarSign,
    Clock,
    CheckCircle,
    X,
    Loader
} from 'lucide-react';
import { purchasesAPI } from '../../services/garageApi';

const Purchases = () => {
    const navigate = useNavigate();
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        supplier_id: '',
        status: '',
        date_from: '',
        date_to: '',
    });

    const fetchPurchases = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await purchasesAPI.getAll(filters);
            const data = response?.data;
            let list = [];
            if (Array.isArray(data)) {
                list = data;
            } else if (data?.data && Array.isArray(data.data)) {
                list = data.data;
            }
            setPurchases(list);
        } catch (err) {
            console.error('Error fetching purchases:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load purchase orders');
            setPurchases([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchPurchases();
    }, [fetchPurchases]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this purchase order? This will reverse inventory changes.')) return;
        try {
            await purchasesAPI.delete(id);
            fetchPurchases();
        } catch (err) {
            console.error('Error deleting purchase:', err);
            const message = err.response?.data?.message || err.response?.data?.error || 'Failed to delete purchase order.';
            alert('Error: ' + message);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ supplier_id: '', status: '', date_from: '', date_to: '' });
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    const getStatusBadge = (status) => {
        const config = {
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
            paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Paid' },
            cancelled: { color: 'bg-red-100 text-red-800', icon: X, label: 'Cancelled' },
        };
        return config[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status || 'Unknown' };
    };

    const stats = useMemo(() => ({
        total: purchases.length,
        pending: purchases.filter(p => p.status === 'pending').length,
        paid: purchases.filter(p => p.status === 'paid').length,
        totalValue: purchases.reduce((sum, p) => sum + Number(p.total_amount || 0), 0),
    }), [purchases]);

    if (loading && purchases.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center">
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Loading purchase orders...
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <Package className="h-6 w-6 text-blue-600" />
                    <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
                </div>
                <button
                    onClick={() => navigate('/purchases/create')}
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    New Purchase Order
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-blue-100 rounded-full">
                            <Package className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Total Orders</p>
                            <p className="text-xl font-bold text-gray-900">{stats.total}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-full">
                            <Clock className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Pending</p>
                            <p className="text-xl font-bold text-yellow-600">{stats.pending}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-green-100 rounded-full">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Paid</p>
                            <p className="text-xl font-bold text-green-600">{stats.paid}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-purple-100 rounded-full">
                            <DollarSign className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Total Value</p>
                            <p className="text-xl font-bold text-purple-600">${stats.totalValue.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                    <div className="flex items-center space-x-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error}</span>
                        <button onClick={fetchPurchases} className="ml-2 underline hover:no-underline">
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {/* Filters + Table */}
            <div className="bg-white shadow rounded-lg">
                <div className="p-4 border-b border-gray-200">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                        <select
                            name="status"
                            value={filters.status}
                            onChange={handleFilterChange}
                            className="form-select rounded-md border-gray-300"
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <input
                            type="date"
                            name="date_from"
                            value={filters.date_from}
                            onChange={handleFilterChange}
                            className="form-input rounded-md border-gray-300"
                            placeholder="From date"
                        />
                        <input
                            type="date"
                            name="date_to"
                            value={filters.date_to}
                            onChange={handleFilterChange}
                            className="form-input rounded-md border-gray-300"
                            placeholder="To date"
                        />
                        <div />
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <X className="h-4 w-4 mr-1" />
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PO #</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {purchases.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                        <p className="text-gray-500">No purchase orders found</p>
                                        {hasActiveFilters && (
                                            <button
                                                onClick={clearFilters}
                                                className="mt-2 text-sm text-blue-600 hover:underline"
                                            >
                                                Clear all filters
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                purchases.map((purchase) => {
                                    const badge = getStatusBadge(purchase.status);
                                    const BadgeIcon = badge.icon;
                                    return (
                                        <tr key={purchase.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">{purchase.id}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {purchase.supplier?.name || 'Unknown'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {purchase.purchase_date ? new Date(purchase.purchase_date).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {purchase.location?.name || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                                                ${Number(purchase.total_amount || 0).toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                                                    <BadgeIcon className="h-3 w-3" />
                                                    <span>{badge.label}</span>
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <button
                                                        onClick={() => navigate(`/purchases/${purchase.id}`)}
                                                        className="text-blue-600 hover:text-blue-900 p-1"
                                                        title="View"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    {purchase.status !== 'paid' && (
                                                        <button
                                                            onClick={() => handleDelete(purchase.id)}
                                                            className="text-red-600 hover:text-red-900 p-1"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Purchases;
